import type { IconOwnProps } from '@ps/isp-web-component-library';
import { Icon } from '@ps/isp-web-component-library';
import { InPlay } from '@ps/isp-web-component-library/icons/InPlay';
import type { TranslationTag } from '@ps/web-core-intl';
import { Translation } from '@ps/web-shared-components';
import type { TennisStatusReason } from '@ps/web-shared-generated-graphql/sports';
import classnames from 'clsx';
import React from 'react';

import AnimatedInPlayIcon from '@swf/sports/components/AnimatedInPlayIcon/AnimatedInPlayIcon';

import type { EventStatusSize } from '../../types';

import {
  TXTCLI_Break,
  TXTCLI_Ended,
  TXTCLI_Finished,
  TXTWCL_Disqualification,
  TXTWCL_ET___Half_Time,
  TXTWCL_ET__extra_time,
  TXTWCL_Full_Time,
  TXTWCL_Going_In_PlayXX,
  TXTWCL_Half_Time,
  TXTWCL_Heat_Delay,
  TXTWCL_In_Play,
  TXTWCL_In_Play_Score_Unavailable,
  TXTWCL_In_Play_in_N0_Min,
  TXTWCL_In_Play_in_N0_Mins,
  TXTWCL_Penalties,
  TXTWCL_Play_suspended,
  TXTWCL_Postponed,
  TXTWCL_Rain_Delay,
  TXTWCL_Retirement,
  TXTWCL_Tie_break
} from './EventStatusMessage.messages.json';
import styles from './EventStatusMessage.scss';

type MessageId =
  | TennisStatusReason
  | 'COMPLETE'
  | 'EXTRA_TIME_HALF_TIME'
  | 'EXTRA_TIME'
  | 'FULL_TIME'
  | 'GOING_IN_PLAY'
  | 'HALF_TIME'
  | 'IN_PLAY'
  | 'NO_SCORE_DATA'
  | 'PENALTIES'
  | 'PLAY_SUSPENDED'
  | 'POSTPONED'
  | 'TIE_BREAK';

type PluralMessageId = 'STARTS_IN_X_MINS';

const translations: Record<MessageId, TranslationTag> = {
  COMPLETE: TXTCLI_Ended,
  DISQUALIFICATION: TXTWCL_Disqualification,
  EXTRA_TIME_HALF_TIME: TXTWCL_ET___Half_Time,
  EXTRA_TIME: TXTWCL_ET__extra_time,
  FINISHED: TXTCLI_Finished,
  FULL_TIME: TXTWCL_Full_Time,
  GOING_IN_PLAY: TXTWCL_Going_In_PlayXX,
  HALF_TIME: TXTWCL_Half_Time,
  HEAT_DELAY: TXTWCL_Heat_Delay,
  IN_PLAY: TXTWCL_In_Play,
  NO_SCORE_DATA: TXTWCL_In_Play_Score_Unavailable,
  ON_COURT_COACHING: TXTCLI_Break,
  PENALTIES: TXTWCL_Penalties,
  PLAY_SUSPENDED: TXTWCL_Play_suspended,
  POSTPONED: TXTWCL_Postponed,
  RAIN_DELAY: TXTWCL_Rain_Delay,
  TIE_BREAK: TXTWCL_Tie_break,
  RETIREMENT: TXTWCL_Retirement,
  TOILET_BREAK: TXTCLI_Break
};

const pluralTranslations: Record<
  PluralMessageId,
  [singular: TranslationTag, plural: TranslationTag]
> = {
  STARTS_IN_X_MINS: [TXTWCL_In_Play_in_N0_Min, TXTWCL_In_Play_in_N0_Mins]
};

function isPluralMessageId(messageId: MessageId | PluralMessageId): messageId is PluralMessageId {
  return messageId in pluralTranslations;
}

export type Props =
  | {
      eventScoreComponent?: never;
      icon?: IconOwnProps['icon'];
      messageId: MessageId;
      size: EventStatusSize;
      values?: never;
    }
  | {
      eventScoreComponent?: never;
      icon?: IconOwnProps['icon'];
      messageId: PluralMessageId;
      size: EventStatusSize;
      values: { minutes: number };
    }
  | {
      eventScoreComponent: React.ReactElement;
      icon?: IconOwnProps['icon'];
      messageId?: never;
      size: EventStatusSize;
      values?: never;
    }
  | {
      eventScoreComponent: React.ReactElement;
      icon?: IconOwnProps['icon'];
      messageId?: MessageId;
      size: EventStatusSize;
      values?: never;
    };

const EventStatusMessage: React.FC<Props> = ({
  eventScoreComponent,
  icon,
  messageId,
  size,
  values
}) => (
  <div
    className={classnames(styles.inPlay, {
      [styles.inPlayXs]: size === 'XSmall',
      [styles.inPlaySm]: size === 'Small'
    })}
  >
    {icon ? (
      icon === InPlay ? (
        <AnimatedInPlayIcon className={styles.inPlayIcon} />
      ) : (
        <Icon icon={icon} className={styles.inPlayIcon} size="sm" />
      )
    ) : null}
    {messageId &&
      (isPluralMessageId(messageId) ? (
        <Translation
          {...pluralTranslations[messageId][values?.minutes === 1 ? 0 : 1]}
          values={values}
        />
      ) : (
        <Translation {...translations[messageId as MessageId]} />
      ))}
    {eventScoreComponent && messageId && <span className={styles.separator}> - </span>}
    {eventScoreComponent && eventScoreComponent}
  </div>
);

export default EventStatusMessage;
